import { PlusIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { GlobalConfigType } from 'actions'
import cloneDeep from 'clone-deep'
import { TargetBox } from 'components/DragDrop'
import type { BaseFile } from 'config'
import { useEffect, useRef, useState } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend, NativeTypes } from 'react-dnd-html5-backend'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import { openS3Document, uploadFiles } from 'services'
import { svgLoading } from 'stories/assets'
import { setLoanNumber } from 'utils/setLoanNumber'

interface InputFileAddonProps {
  /**
   * S3 upload path. Defaults to loan/{loanNumber}/document.
   */
  filePath?: string
  /**
   * Current uploaded files
   */
  value?: BaseFile[]
  /**
   * Callback when files change (upload or delete)
   */
  onChange: (files: BaseFile[]) => void
  /**
   * Allow selecting multiple files at once
   */
  multiple?: boolean
  /**
   * Accepted MIME types for the file input
   */
  acceptFileTypes?: string
  /**
   * Disable upload and delete
   */
  disabled?: boolean
}

export const InputFileAddon = ({
  filePath: _filePath = '',
  value = [],
  onChange,
  multiple = true,
  acceptFileTypes = 'application/pdf',
  disabled = false,
}: InputFileAddonProps) => {
  const rulesConfig = useSelector((state: any) => state.globalConfigReducer[GlobalConfigType.RulesConfig])
  const { fileExtensions: acceptedFileTypes } = rulesConfig

  const [isLoading, setLoading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [filePath, setFilePath] = useState('')

  useEffect(() => {
    if (_filePath) setFilePath(_filePath)
    else {
      const loanNumber = setLoanNumber()
      setFilePath(`loan/${loanNumber}/document`)
    }
  }, [_filePath])

  const uploadDocumentFiles = (files: File[]) => {
    const data = { path: filePath }
    const acceptedFiles: File[] = []

    files.forEach((file) => {
      const ext = file.name.toLowerCase().split('.').pop() || ''
      if (acceptedFileTypes.includes(ext)) {
        acceptedFiles.push(file)
      } else {
        toast(`${file.name} is not ${acceptedFileTypes.join(', ')}`, { type: 'error' })
      }
    })

    if (!acceptedFiles.length) return

    setLoading(true)
    uploadFiles(data, acceptedFiles).then((keys: string[]) => {
      const newData = cloneDeep(value)
      const id = Date.now()
      keys.forEach((key, index) => {
        newData.push({
          id: id + index,
          name: acceptedFiles[index].name,
          fileKey: key,
          createdAt: Date.now(),
        })
      })
      setLoading(false)
      onChange(newData)
    })
  }

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target
    if (!files || files.length === 0) return
    uploadDocumentFiles(Array.from(files))
    e.target.value = ''
  }

  const onRemoveFile = (e: React.MouseEvent, index: number) => {
    e.preventDefault()
    e.stopPropagation()
    const newData = cloneDeep(value)
    newData.splice(index, 1)
    onChange(newData)
  }

  const onDropDocument = async (document: any) => {
    if (document.files) {
      uploadDocumentFiles(document.files)
    } else {
      let exist = false
      const temp = cloneDeep(value)
      if (document.key && !document.fileKey) document.fileKey = document.key
      if (!document.fileKey) return

      temp.map((item: BaseFile) => {
        if (item.fileKey === document.fileKey) exist = true
      })
      if (!exist)
        temp.push({
          ...document,
        })
      onChange(temp)
    }
  }

  const renderContent = () => (
    <span className="relative inline-flex flex-wrap items-center gap-1">
      {!disabled && (
        <>
          <span
            className="cursor-pointer text-shade-blue hover:bg-gray-200 rounded p-0.5 inline-flex items-center"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              fileInputRef.current?.click()
            }}
            title="Upload files"
          >
            <PlusIcon className="w-3.5 h-3.5" />
          </span>
          <input
            ref={fileInputRef}
            className="hidden"
            type="file"
            accept={acceptFileTypes}
            multiple={multiple}
            onChange={onFileChange}
          />
        </>
      )}

      {isLoading && <img src={svgLoading} className="inline w-3.5 h-3.5 animate-spin" alt="uploading" />}

      {value.map((file, index) => (
        <span
          key={file.id || index}
          className="inline-flex items-center gap-0.5 text-[11px] text-shade-blue bg-gray-50 border border-gray-200 rounded px-1 max-w-[150px] hover:bg-gray-100 leading-tight"
        >
          <span
            className="truncate cursor-pointer hover:underline"
            title={file.name}
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              openS3Document(file.fileKey)
            }}
          >
            {file.name}
          </span>
          {!disabled && (
            <span
              className="cursor-pointer text-red-500 hover:text-red-700 flex-shrink-0"
              onClick={(e) => onRemoveFile(e, index)}
            >
              <XMarkIcon className="w-2.5 h-2.5" />
            </span>
          )}
        </span>
      ))}
    </span>
  )

  if (disabled) return renderContent()

  return (
    <DndProvider backend={HTML5Backend}>
      <TargetBox types={['document', NativeTypes.FILE]} onDrop={(document: any) => onDropDocument(document)}>
        {renderContent()}
      </TargetBox>
    </DndProvider>
  )
}
